{
  "title": "Policies",
  "description": "Bounded rule sets that decide, for every agent tool call, whether it runs, waits for a person, or is refused.",
  "newPolicy": "New Policy",
  "backToList": "Back to Policies",
  "noDataTitle": "No Policies Yet",
  "noDataMessage": "Click “New Policy” above to create your first policy.",
  "noDataMessageReadOnly": "Policies created for this account will appear here.",
  "fetchFailed": "An error occurred while trying to fetch policies.",
  "createSuccess": "Policy created successfully.",
  "createFailed": "An error occurred while trying to create the policy.",
  "deletePolicy": "Delete Policy",
  "deleteHeldTooltip": "Another editor holds this policy's draft. It can be deleted once the hold is released or lapses.",
  "deleteModalTitle": "Are you sure you want to delete the <strong>{{name}}</strong> policy?",
  "deleteSuccess": "Policy deleted.",
  "deleteFailed": "An error occurred while trying to delete the policy.",
  "table": {
    "policy": "Policy",
    "rules": "Rules",
    "agents": "Agents",
    "approvers": "Approvers",
    "noApprovers": "None",
    "unknownRole": "Role {{id}}",
    "fallthrough": "Fallthrough",
    "draft": "Draft",
    "warnings": "Warnings",
    "created": "Created",
    "clean": "Clean",
    "warningCount": "{{count}} warning",
    "warningCount_plural": "{{count}} warnings",
    "view": "View"
  },
  "detail": {
    "fetchFailed": "An error occurred while trying to fetch the policy.",
    "notFoundTitle": "Policy Not Found",
    "notFoundMessage": "This policy may have been deleted, or the link may be wrong.",
    "justCreated": "Compiled and stored. Decisions will be recorded against each rule id below.",
    "policyId": "Policy ID",
    "revision": "Revision",
    "created": "Created",
    "agents": "Agents",
    "noAgents": "None",
    "approvers": "Approvers",
    "noApprovers": "None",
    "viewRole": "View role",
    "editRole": "Edit role",
    "notifications": "Slack notifications",
    "noNotifications": "None",
    "compiledRules": "Compiled Rules",
    "fallthroughPrefix": "Anything no rule above covers:",
    "viewHistory": "View history",
    "runEvaluation": "Run Evaluation"
  },
  "compiled": {
    "expand": "Expand Rule",
    "collapse": "Collapse Rule"
  },
  "revisions": {
    "title": "Revision History",
    "description": "Every accepted draft, fork, or revert appends a frozen snapshot of the rules and fallthrough. The log is append-only, so restoring an older revision adds a new one rather than rewinding.",
    "fetchFailed": "An error occurred while trying to fetch the policy's revisions.",
    "empty": "No revisions recorded yet.",
    "current": "Current",
    "revisionLabel": "Revision {{id}}",
    "origin": {
      "create": "Created",
      "edit": "Edited",
      "fork": "Forked",
      "revert": "Reverted"
    },
    "provenance": {
      "restoredFrom": "Restored from {{id}}",
      "forkedFrom": "Forked from {{id}}",
      "createdBy": "by {{id}}"
    },
    "ruleCount": "{{count}} rule",
    "ruleCount_plural": "{{count}} rules",
    "fallthrough": "Fallthrough",
    "diff": {
      "same": "Same rules and fallthrough as the current policy",
      "lead": "Restoring would:",
      "add": "add {{count}} rule",
      "add_plural": "add {{count}} rules",
      "change": "change {{count}} rule",
      "change_plural": "change {{count}} rules",
      "remove": "drop {{count}} current rule",
      "remove_plural": "drop {{count}} current rules",
      "fallthrough": "change the fallthrough",
      "badge": {
        "added": "Not in current",
        "changed": "Differs from current"
      },
      "removedTitle": "Current rules this revision does not have",
      "removedHint": "These would be dropped by a revert, or start out absent from a forked draft."
    },
    "viewRules": "View Rules",
    "hideRules": "Hide Rules",
    "fallthroughPrefix": "Anything no rule in this revision covers:",
    "fork": "Fork into Draft",
    "forkHint": "Opens a draft pre-filled from this revision so you can review and edit before it lands. Name and description stay as they are now.",
    "forkLockedTooltip": "A draft is already open. There is one draft per policy, so discard it before forking a revision.",
    "revert": "Revert to This",
    "revertHint": "Restores this revision's rules and fallthrough immediately, with no draft or review step.",
    "revertModalTitle": "Revert <strong>{{name}}</strong> to revision <strong>{{id}}</strong>?",
    "revertModalBody": "This lands immediately as a new revision: the policy's rules and fallthrough are replaced with this revision's, and every agent assigned to the policy is governed by them from the next evaluation. The name and description are kept.",
    "revertModalReviewInstead": "To review or edit the restored rules first, fork the revision into a draft instead.",
    "revertModalDraftWarning": "An open draft is anchored to the current revision. Reverting makes it stale, so accepting it later would overwrite this revert.",
    "revertModalOk": "Yes, Revert",
    "forked": "Draft opened from the selected revision. Review it, then accept to restore.",
    "forkFailed": "An error occurred while trying to fork the revision.",
    "reverted": "Policy reverted. It is now at a new revision.",
    "revertFailed": "An error occurred while trying to revert the policy."
  },
  "draft": {
    "stateBadge": {
      "none": "No draft",
      "mine": "Your draft",
      "held": "Held by another editor",
      "lockedUntil": "Locked until {{expires}}",
      "lapsed": "Hold lapsed"
    },
    "heldBy": "Held by {{name}}",
    "heldByYou": "Held by you",
    "banner": {
      "noneTitle": "Read only",
      "noneBody": "This policy is never edited in place. Create a draft to author changes, then accept it to project them back.",
      "createDraft": "Create Draft",
      "mineTitle": "You hold the draft",
      "mineBody": "You are the only editor. The hold renews every time you save, and expires {{expires}}.",
      "mineForkedTitle": "You hold a draft forked from revision {{id}}",
      "mineForkedBody": "Its rules and fallthrough started as a copy of that revision; the name and description are the policy's current ones. Accepting it restores those rules at a new revision. You are the only editor, and the hold expires {{expires}}.",
      "staleTitle": "Your draft is stale",
      "staleBody": "The policy moved on while this draft sat idle, so its base revision is no longer current. Accepting it would overwrite whatever landed in between.",
      "continueEditing": "Continue Editing",
      "acceptDraft": "Accept Draft",
      "discardDraft": "Discard Draft",
      "heldTitle": "Held by another editor",
      "heldByTitle": "Held by {{name}}",
      "heldBody": "There is one draft per policy, so only the current editor can write to it right now. The hold lapses {{expires}}, after which it can be taken over.",
      "lapsedTitle": "The editing hold has lapsed",
      "lapsedByTitle": "{{name}}'s editing hold has lapsed",
      "lapsedBody": "Taking it over replaces the previous editor's unaccepted changes with a fresh copy of the policy. Their work is not merged. Discarding deletes their unaccepted changes and leaves the policy without a draft.",
      "lapsedMineTitle": "Your editing hold has lapsed",
      "lapsedMineBody": "This is your draft, and your unaccepted changes are still here. Continue editing to renew the hold and pick up where you left off — until then, another editor can take the draft over.",
      "takeOver": "Take Over Draft"
    },
    "preview": {
      "title": "Draft",
      "show": "View Draft",
      "hide": "Hide Draft",
      "updated": "Last saved {{date}}",
      "forkedFrom": "Forked from revision {{id}}",
      "fallthroughPrefix": "Anything no drafted rule covers:"
    },
    "expiresInMinutes": "{{count}} minutes from now",
    "expiresInOneMinute": "in under a minute",
    "expiresAlready": "any moment",
    "tookOver": "Hold taken over. The previous editor's unaccepted changes were replaced with a fresh copy of the policy.",
    "saved": "Draft saved. The hold was renewed.",
    "accepted": "Draft accepted. The policy is now at a new revision.",
    "discarded": "Draft discarded. The policy is unchanged.",
    "acquireFailed": "An error occurred while trying to open the policy draft.",
    "fetchFailed": "An error occurred while trying to fetch the policy draft.",
    "saveFailed": "An error occurred while trying to save the draft.",
    "acceptFailed": "An error occurred while trying to accept the draft.",
    "discardFailed": "An error occurred while trying to discard the draft."
  },
  "recourse": {
    "allow": "Allow",
    "approval_required": "Approval",
    "deny": "Deny"
  },
  "wizard": {
    "title": "New Policy",
    "continue": "Continue",
    "create": "Create Policy",
    "editingDraft": "Editing Draft",
    "editingFork": "Forked from revision {{id}}",
    "holdNote": "Hold renews on save",
    "saveDraft": "Save Draft",
    "saveAndAccept": "Save and Accept Draft",
    "stepEyebrow": "Step {{num}}",
    "steps": {
      "basics": "Basics",
      "fallthrough": "Fallthrough",
      "rules": "Rules",
      "approvers": "Approvers",
      "review": "Review"
    },
    "stepHints": {
      "basics": "Name and description",
      "fallthrough": "Unmatched calls",
      "oneRule": "1 rule",
      "ruleCount": "{{count}} rules",
      "approvers": "{{name}}",
      "approversNone": "Optional",
      "review": "Compile and create"
    },
    "blocker": {
      "name": "Name needs at least 2 characters",
      "fields": "Fix the highlighted fields to continue",
      "fallthrough": "Pick what happens to unmatched calls",
      "rule": "Rule {{num}} is incomplete — check what it matches on, its values and its outcome",
      "channels": "Remove the disconnected Slack channel to continue"
    },
    "basics": {
      "title": "Name this policy",
      "description": "A policy is a versioned set of bounded rules. The name is how reviewers and the decision log refer to it, so make it specific.",
      "nameLabel": "Policy Name",
      "nameHelp": "Must be unique in this account. 2–128 characters, e.g. \"k8s governance baseline\"",
      "nameRequired": "Policy name is required.",
      "nameMinLength": "Policy name must be at least {{length}} characters.",
      "descriptionLabel": "Description",
      "descriptionHelp": "Optional summary of what this policy governs, e.g. \"Guards kubectl access for the platform agents\"",
      "descriptionMinLength": "Description must be at least {{length}} characters."
    },
    "fallthrough": {
      "title": "What happens to a call no rule permits?",
      "description": "There is no default here on purpose. Denying a call, routing it to a person, or waving it through is too consequential to inherit silently.",
      "denyTitle": "Deny Outright",
      "denyDescription": "The call fails immediately with your code and message. Nothing reaches a human queue.",
      "approvalTitle": "Send for Approval",
      "approvalDescription": "Unmatched calls pause and route to an approver. Safer for rollout, noisier for on-call.",
      "allowTitle": "Allow",
      "allowDescription": "Unmatched calls go through untouched. Rules become the exceptions, so only pick this for a policy that exists to block specific calls.",
      "allowNote": "Allowed fallthrough calls are still recorded in the audit log.",
      "codeLabel": "Code",
      "codeHelp": "Machine-stable code clients branch on for a fallthrough denial. Up to 35 characters, e.g. \"NO_MATCHING_RULE\"",
      "codeMaxLength": "Code must be at most {{length}} characters.",
      "messageLabel": "Message Shown to the Caller",
      "messageHelp": "User-facing text for a fallthrough denial. Up to 256 characters, e.g. \"No rule in this policy covers this request.\"",
      "messageMaxLength": "Message must be at most {{length}} characters."
    },
    "rules": {
      "title": "Rules",
      "description": "Each rule matches on a tool namespace, a tool name, argument values, or any combination of them, and produces one outcome. Evaluation is order-independent, so rules can be read in any order, and a deny cannot be undone by an ordinary approval.",
      "addRule": "Add Rule",
      "collapseCompleted": "Collapse completed",
      "expandAll": "Expand all",
      "noRulesTitle": "No rules yet",
      "noRulesMessage": "A policy with no rules sends every call to its fallthrough."
    },
    "approvers": {
      "title": "Who can approve calls this policy holds?",
      "description": "When a rule or the fallthrough sends a call for approval, members of this role are the ones who can approve or deny it. Optional, but recommended so held calls have a clear owner.",
      "selectLabel": "Approving role",
      "selectHelp": "Optional, but recommended. Choose the role whose members handle approvals for this policy, or leave it as “No approvers”.",
      "noneOption": "Anyone can approve",
      "createRole": "Create a role",
      "editRole": "Edit role",
      "viewRole": "View role",
      "selectedSlug": "Slug: {{slug}}",
      "empty": {
        "title": "No roles yet",
        "message": "Create a role so a group can approve the calls this policy sends for review."
      },
      "modal": {
        "title": "Create an approving role",
        "nameLabel": "Role name",
        "namePlaceholder": "e.g. Platform Approvers",
        "nameRequired": "Role name is required.",
        "grantsSummary": "This role can approve or deny AI execution requests and view the policies that raised them.",
        "inheritLabel": "Inherit from",
        "inheritHelp": "Optionally start from another role's permissions in addition to the approval grants above.",
        "inheritNone": "Nothing — approvals only",
        "advancedLinkDescription": "Need to add scopes or a full permission set?",
        "advancedLinkButtonText": "Create a role the long way",
        "submit": "Create role",
        "noWrite": "You need role management access to create a role."
      }
    },
    "notifications": {
      "title": "Slack notifications",
      "description": "Optional. When this policy holds a call for approval, a notice with a link to review it is posted to each channel below.",
      "loadFailed": "The account's Slack channels could not be loaded. Refresh the list to try again.",
      "empty": {
        "title": "No Slack channels connected",
        "message": "Connect a channel or direct message under Settings > Integrations > Slack, then refresh the list to add it here."
      },
      "addLabel": "Add a Slack channel",
      "channelLimit": "A policy can notify up to {{max}} channels.",
      "allAdded": "Every connected channel is already added.",
      "manage": "Manage Slack channels",
      "refresh": "Refresh list",
      "privateChannel": "Private channel",
      "unnamedChannel": "Slack channel",
      "remove": "Remove",
      "disconnectedLabel": "Disconnected channel",
      "disconnected": "This channel is no longer connected to the account's Slack integration. Remove it to continue.",
      "broadcastLabel": "Also notify",
      "broadcastHelp": "A posted notice alerts nobody on its own unless they follow every message in the channel. Choose who it should also notify.",
      "broadcast": {
        "none": "Nobody",
        "here": "@here: members active right now",
        "channel": "@channel: every member"
      },
      "mentionIdLabel": "Slack member or user group ID",
      "mentionIdPlaceholder": "U012AB3CD or S0614TZR7",
      "mentionHandleLabel": "Label (optional)",
      "mentionAdd": "Add mention",
      "mentionSubmit": "Add",
      "mentionRemove": "Remove {{name}}",
      "mentionInvalid": "Enter a Slack ID: U, W or S followed by capital letters and digits.",
      "mentionDuplicate": "That ID is already mentioned in this channel.",
      "mentionLimit": "A channel can carry up to {{max}} mentions.",
      "mentionHelp": "People and user groups are mentioned by Slack ID, because Slack notifies on the ID and not the handle. A member ID starts with U or W: open their Slack profile and choose Copy member ID from its menu. A user group ID starts with S and is in the URL shown when viewing the group. The label is only shown here."
    },
    "review": {
      "title": "Review and create",
      "description": "Rules are compiled and validated before the policy is stored. An invalid policy is rejected rather than saved, so there is no half-valid state to fix later.",
      "nameLabel": "Name",
      "descriptionLabel": "Description",
      "descriptionEmpty": "Not set",
      "fallthroughLabel": "Unmatched Calls",
      "approversLabel": "Approvers",
      "approversEmpty": "None",
      "notificationsLabel": "Slack notifications",
      "notificationsEmpty": "None",
      "rulesLabel": "Rules",
      "rulesEmpty": "No rules. Every call goes to the fallthrough."
    },
    "evaluateDraft": "Evaluate Draft"
  },
  "rule": {
    "title": "Rule {{num}}",
    "nameLabel": "Name",
    "nameHelp": "Optional label for this rule, shown wherever the rule is listed. Up to {{length}} characters, e.g. \"Block destructive kubectl\".",
    "nameMaxLength": "Name must be at most {{length}} characters.",
    "chooseOutcome": "Choose outcome",
    "matchOnLabel": "Match On",
    "matchOnHelp": "Pick one or more. Every selected match must hold for the rule to apply.",
    "matchRequired": "Select at least one thing for this rule to match on.",
    "match": {
      "tool_namespace": {
        "title": "Tool Namespace",
        "description": "Every tool in a namespace, e.g. k8s_mcp."
      },
      "tool_name": {
        "title": "Tool Name",
        "description": "One specific tool, e.g. delete. Add a namespace to pin down which one."
      },
      "arguments": {
        "title": "Tool-Call Arguments",
        "description": "Values the agent passed, each compared with its own operator."
      }
    },
    "toolNameLabel": "Tool Name",
    "toolNameHelp": "The exact tool name this rule matches, e.g. \"delete\", or a list of names under \"is one of\" and \"is not one of\". Up to 512 characters each.",
    "toolNamespaceLabel": "Namespace",
    "toolNamespaceHelp": "The tool namespace this rule matches, e.g. \"k8s_mcp\", or a list of namespaces under \"is one of\" and \"is not one of\". Up to 512 characters each.",
    "argNameLabel": "Argument Name",
    "opLabel": "Comparison",
    "op": {
      "eq": "is (eq)",
      "neq": "is not (neq)",
      "gt": "greater than (gt)",
      "gte": "at least (gte)",
      "lt": "less than (lt)",
      "lte": "at most (lte)",
      "in": "is one of (in)",
      "nin": "is not one of (nin)"
    },
    "valueLabel": "Value",
    "valuesLabel": "Values",
    "valuesPlaceholder": "Type a value and press Enter",
    "valuesAddPlaceholder": "Add another…",
    "valuesTagPlaceholder": "Press Enter to add",
    "valueRequired": "A value is required.",
    "valueMaxLength": "Value must be at most {{length}} characters.",
    "argsLabel": "Arguments to Match",
    "argsHelp": "Tool-call arguments that must hold, each compared with its own operator, e.g. env is prod. Numbers and true/false are sent as numbers and booleans.",
    "argsAdd": "Argument",
    "argsRemove": "Remove argument",
    "argsRowInvalid": "Each argument needs a valid name, a comparison, and at least one value.",
    "argsRequired": "Add at least one argument, or deselect Tool-Call Arguments.",
    "outcomeLabel": "Outcome When This Rule Matches",
    "outcomeAllow": "Allow",
    "outcomeApproval": "Require Approval",
    "outcomeDeny": "Deny",
    "outcomeRequired": "Choose an outcome for this rule.",
    "codeLabel": "Code",
    "codeHelp": "Machine-stable code clients branch on when this rule blocks a call. Up to 35 characters, e.g. \"FORBIDDEN_TOOL\"",
    "codeMaxLength": "Code must be at most {{length}} characters.",
    "messageLabel": "Message Shown to the Caller",
    "messageHelp": "User-facing text explaining why this rule blocked the call. Up to 256 characters, e.g. \"Restarting a rollout needs a human approver before it can run.\"",
    "messageMaxLength": "Message must be at most {{length}} characters.",
    "duplicate": "Duplicate",
    "remove": "Remove",
    "collapse": "Collapse rule",
    "expand": "Expand rule",
    "collapseIncomplete": "Finish this rule to collapse it.",
    "dragHandle": "Drag to reorder",
    "showCedar": "View compiled rule",
    "hideCedar": "Hide compiled rule",
    "sentence": {
      "toolName": "the tool",
      "toolNamespace": "the tool namespace",
      "argument": "argument \"{{name}}\"",
      "and": "and",
      "noConditions": "nothing is selected to match",
      "op": {
        "eq": "is",
        "neq": "is not",
        "gt": "is greater than",
        "gte": "is at least",
        "lt": "is less than",
        "lte": "is at most",
        "in": "is one of",
        "nin": "is not one of"
      },
      "outcome": {
        "allow": "allow the call",
        "approval_required": "send it to an approver",
        "deny": "refuse the call",
        "unset": "choose an outcome"
      },
      "template": "When {{condition}} → {{outcome}}."
    }
  },
  "fallthroughSentence": {
    "unset": "Not chosen yet.",
    "allow": "Allowed through untouched",
    "deny": "Denied outright with {{code}}: “{{message}}”",
    "approval_required": "Sent to an approver, tagged {{code}}: “{{message}}”",
    "rest": {
      "allow": "through untouched",
      "deny": "outright with {{code}}: “{{message}}”",
      "approval_required": "tagged {{code}}: “{{message}}”"
    }
  },
  "findings": {
    "ready": "Validation clean. This policy will compile as written.",
    "emptyRuleSet": "This policy has no rules, so every call goes straight to the fallthrough.",
    "noPermitRule": "No rule allows anything and unmatched calls are denied, so this policy can never let a call through.",
    "duplicateMatch": "Two rules match the same field and value. Since evaluation is order-independent, the stricter outcome wins.",
    "missingCode": "A blocking rule has no code, so clients cannot branch on the refusal."
  },
  "evaluate": {
    "title": "Evaluate Tool Calls",
    "description": "Send sample tool calls through the compiled rules and read back the verdict per call and per rule. Nothing is saved and no workflow is recorded, so tweak the arguments and resend as often as you like.",
    "failed": "An error occurred while trying to evaluate the policy.",
    "backToPolicy": "Policy",
    "backToDraft": "Back to Draft",
    "targetLabel": "Evaluate Against",
    "target": {
      "policy": "Live Policy",
      "draft": "Draft"
    },
    "noDraftTarget": "This policy has no draft to evaluate.",
    "draftNote": "Running against the stored draft. Its rules are compiled on the fly and the draft itself is untouched.",
    "agentLabel": "Evaluate as Agent",
    "agentHelp": "The policy is evaluated as this agent. Only active agents assigned to the policy can be chosen; the service defaults to the oldest one.",
    "agentDefault": "{{name}} (default)",
    "noActiveAgent": "No active agent is assigned to this policy. A placeholder principal is used instead, which cannot change a verdict.",
    "calls": {
      "title": "Tool Calls",
      "count": "{{count}} call · up to {{max}}",
      "count_plural": "{{count}} calls · up to {{max}}",
      "fillFromRules": "Fill from Rules",
      "add": "Add Tool Call",
      "callTitle": "Call {{num}}",
      "toolNameLabel": "Tool Name",
      "toolNameHelp": "The tool the agent asked to run, e.g. \"kubectl.rollout_restart\". The text before the first dot is sent as the tool namespace.",
      "namespaceHint": "Namespace resolves to {{namespace}}",
      "namespaceNone": "none",
      "argsLabel": "Arguments",
      "argsHelp": "The arguments the agent supplied with the call, as name/value pairs. Numbers and true/false are sent as numbers and booleans.",
      "argNameLabel": "Argument name",
      "argValueLabel": "Value",
      "addArg": "Argument",
      "removeArg": "Remove argument",
      "duplicate": "Duplicate",
      "remove": "Remove",
      "collapse": "Collapse call",
      "expand": "Expand call",
      "collapseIncomplete": "Give this call a tool name to collapse it.",
      "collapseAll": "Collapse all",
      "expandAll": "Expand all",
      "noToolName": "No tool name yet",
      "empty": "No tool calls yet. Add one, or fill the list from the rules being evaluated."
    },
    "blocker": {
      "empty": "Add at least one tool call",
      "toolName": "Call {{num}} needs a tool name",
      "argName": "Call {{num}} has an argument without a name",
      "tooMany": "At most {{max}} tool calls can be evaluated at once"
    },
    "footer": {
      "idle": "Rules are evaluated live, nothing is recorded.",
      "ran": "Nothing was saved. Change anything and run it again."
    },
    "run": "Run Evaluation",
    "runAgain": "Run Again",
    "result": {
      "title": "Result",
      "empty": "No evaluation yet. Describe one or more tool calls, then run them to see the verdict for the batch, for each call, and for every rule that was consulted.",
      "evaluatedAs": "Evaluated as {{name}}",
      "summary": {
        "allow": "All {{count}} call runs as submitted.",
        "allow_plural": "All {{count}} calls run as submitted.",
        "deny": "At least one call is refused, so the batch is denied. The strongest verdict across every item decides the batch.",
        "approval_required": "At least one call needs a person, so the batch waits for an approver."
      },
      "reasonLabel": "Reason shown to the model",
      "failedClosed": "The engine failed closed instead of running the rules:",
      "perCall": "Per Call",
      "perRule": "Per Rule",
      "viaFallthrough": "No rule matched, so the fallthrough decided it.",
      "viaFallthroughCode": "No rule matched, so the fallthrough decided it ({{code}}).",
      "decidedBy": "Decided by {{rule}} · {{matched}}",
      "oneMatched": "1 rule matched",
      "manyMatched": "{{count}} rules matched, the strictest won",
      "rule": {
        "noMatch": "No match",
        "decided": "Decided {{count}} call",
        "decided_plural": "Decided {{count}} calls",
        "overruled": "Matched {{count}}, overruled"
      },
      "fallthroughPrefix": "Fallthrough:"
    }
  },
  "tests": {
    "title": "Test Suite",
    "fetchFailed": "An error occurred while trying to fetch the policy's test suite.",
    "saveFailed": "An error occurred while trying to save the test suite.",
    "runFailed": "An error occurred while trying to run the test suite.",
    "target": {
      "live": "Live Policy",
      "draft": "Draft",
      "both": "Both"
    },
    "expect": {
      "any": "Any",
      "none": "None",
      "perCall": "Per Call"
    },
    "card": {
      "open": "Open Suite",
      "noCases": "Created with this policy · No cases yet",
      "caseCount": "{{count}} case",
      "caseCount_plural": "{{count}} cases",
      "lastRun": "Last run at {{time}}",
      "notRun": "Not run yet",
      "liveTally": "Live · {{passed}} of {{graded}} pass",
      "draftTally": "Draft · {{passed}} of {{graded}} pass"
    },
    "suite": {
      "missingTitle": "No Test Suite",
      "missingMessage": "This policy has no test suite assigned.",
      "count": "{{count}} of {{max}} cases",
      "origin": "Created on {{date}}. One suite per policy; it stays for the life of the policy.",
      "addCase": "Add Case",
      "full": "A suite holds at most {{max}} cases",
      "columns": {
        "case": "Case",
        "expected": "Expected"
      },
      "draftChanges": "Draft changes this",
      "callCount": "{{count}} call",
      "callCount_plural": "{{count}} calls",
      "fromWorkflow": "Promoted from workflow …{{id}}",
      "byHand": "Written by hand",
      "removeCase": "Remove Case",
      "editCase": "Edit Case",
      "exportCase": "Export to Library",
      "exportModalBody": "A copy of this case is saved to the test case library, where any policy's suite can pick it up. The case stays in this suite unchanged.",
      "removeModalTitle": "Remove <strong>{{name}}</strong> from the suite?",
      "caseAdded": "Case added to the suite.",
      "caseSaved": "Case saved.",
      "caseRemoved": "Case removed from the suite.",
      "empty": "No cases yet. Add one by hand, or run an evaluation and save its calls as a case.",
      "filterEmpty": "No cases match this filter."
    },
    "filter": {
      "all": "All {{count}}",
      "failing": "Failing {{count}}",
      "changed": "Draft changes {{count}}",
      "ungraded": "Ungraded {{count}}"
    },
    "run": {
      "targetLabel": "Run Against",
      "run": "Run Suite",
      "note": {
        "noDraft": "This policy has no open draft, so only the live policy can be graded. Create a draft from the policy page to compare a change before you accept it.",
        "both": "Grades every case against the live policy and the draft side by side, so you see exactly what accepting the draft would change.",
        "draft": "Grades the draft only. The draft and its hold are left untouched.",
        "live": "Grades the live policy only."
      },
      "editedSince": "Cases changed since this run. Run again to grade the latest version.",
      "liveSubtitle": "Current accepted revision",
      "draftHeldBy": "Held by {{name}}",
      "ofGraded": "of {{count}} graded case passes",
      "ofGraded_plural": "of {{count}} graded cases pass",
      "passed": "{{count}} passed",
      "failed": "{{count}} failed",
      "ungraded": "{{count}} ungraded",
      "coverage": "Rule coverage",
      "coverageCount": "{{covered}} of {{total}} rules decide a case",
      "uncovered": "No case reaches:",
      "ranAt": "Ran at {{time}}",
      "ranAtDuration": "Ran at {{time}} · {{ms}} ms"
    },
    "stale": {
      "policyChanged": "Policy changed since",
      "draftEdited": "Draft edited since",
      "draftAccepted": "Draft accepted since",
      "draftDiscarded": "Draft discarded"
    },
    "result": {
      "notRun": "Not run",
      "pass": "✓ Pass",
      "fail": "✕ Fail",
      "ungraded": "Ungraded",
      "columns": {
        "target": "Graded Against",
        "verdict": "Verdict",
        "code": "Code",
        "decidedBy": "Decided By"
      },
      "viaRuleId": "Rule {{id}}",
      "viaFallthrough": "No rule matched, so the fallthrough decided it."
    },
    "fromEvaluate": {
      "note": "Keep these calls as a test case. The verdicts above become its expectations, so the suite flags any later change that alters them.",
      "save": "Save as Test Case",
      "saveToLibrary": "Save to Library",
      "libraryBody": "The calls you just evaluated, with the verdicts reached as their expectations, saved as a standalone case any policy can use."
    },
    "case": {
      "backToSuite": "Test Suite",
      "newTitle": "New Test Case",
      "editTitle": "Edit Test Case",
      "notFoundTitle": "Test Case Not Found",
      "notFoundMessage": "This case may have been removed from the suite, or the link may be wrong.",
      "description": "A case is one or more tool calls sent together, the same shape an agent submits. Set what the policy should decide. Anything left on Any is recorded but not graded.",
      "prefilled": "Expectations are prefilled with the verdicts you just saw. Change any that are wrong before saving.",
      "prefilledWorkflow": "Expectations are prefilled with the decisions the policy reached for this workflow. Change any that are wrong before saving.",
      "nameLabel": "Name",
      "descriptionLabel": "Description",
      "descriptionHelp": "Optional. Why this call matters.",
      "expectLabel": "The whole request should roll up to",
      "expectHelp": "The strictest verdict across every call decides the request.",
      "toolNameHelp": "The tool the agent asked to run, e.g. \"kubectl.scale\".",
      "namespaceLabel": "Tool Namespace",
      "namespaceHelp": "The MCP server the tool came from. Left empty, the call is evaluated under \"default\".",
      "callExpectLabel": "This call should be",
      "callExpectHelp": "The outcome this call must produce on its own. Any records the verdict without grading it.",
      "codeLabel": "Expected Code",
      "codeHelp": "Optional. When set, the verdict's code must match as well.",
      "blocker": {
        "name": "Name the case",
        "nameShort": "The name needs at least 2 characters",
        "empty": "Add at least one tool call"
      },
      "footer": "Runs grade this case against the live policy and any open draft.",
      "delete": "Delete Case",
      "cancel": "Cancel",
      "save": "Save Case"
    },
    "library": {
      "title": "Test Cases",
      "description": "Saved test cases that belong to no policy. Promote a recorded workflow or an evaluation here, then copy a case into any policy's suite or run it through any policy.",
      "fetchFailed": "An error occurred while trying to fetch the saved test cases.",
      "saveFailed": "An error occurred while trying to save the test case.",
      "saved": "Saved \"{{name}}\" to the test case library.",
      "fetchCaseFailed": "An error occurred while trying to fetch the test case.",
      "updated": "Test case saved.",
      "deleteFailed": "An error occurred while trying to delete the test case.",
      "deleted": "Test case deleted.",
      "addedToSuite": "Added \"{{name}}\" to the suite.",
      "copied": "{{count}} case added to the suite.",
      "copied_plural": "{{count}} cases added to the suite.",
      "empty": "No saved test cases yet.",
      "emptyTitle": "No Saved Test Cases",
      "emptyMessage": "Write one, or save a recorded workflow or an evaluation as a test case, and it will appear here, ready to copy into any policy's suite.",
      "columns": {
        "source": "Source"
      },
      "evaluate": "Evaluate",
      "addToSuite": "Add to Suite",
      "editCase": "Edit Case",
      "deleteCase": "Delete Case",
      "deleteModalTitle": "Delete <strong>{{name}}</strong> from the library?",
      "deleteModalBody": "A deleted test case cannot be restored. Suites that already use it hold their own copy and keep running it.",
      "addFromLibrary": "Add from Library",
      "newCase": "New Test Case",
      "newDescription": "A case is one or more tool calls sent together, the same shape an agent submits. Set what a policy should decide. Saved here, the case belongs to no policy: copy it into any suite or run it through any policy.",
      "newFooter": "Saved cases can be copied into any policy's suite.",
      "editFooter": "Suites that already use this case hold their own copy and are not changed.",
      "notFoundMessage": "This case may have been deleted, or the link may be wrong.",
      "saveModal": {
        "title": "Save Test Case",
        "summary": "{{count}} call",
        "summary_plural": "{{count}} calls",
        "submit": "Save to Library"
      },
      "policyModal": {
        "evaluateTitle": "Evaluate \"{{name}}\"",
        "evaluateBody": "The case's calls are loaded into the policy's evaluation page, where you can run them and adjust them before running again.",
        "evaluateSubmit": "Open Evaluation",
        "suiteTitle": "Add \"{{name}}\" to a Suite",
        "suiteBody": "A copy of the case is appended to the policy's test suite. The saved case stays in the library unchanged.",
        "suiteSubmit": "Add to Suite",
        "policyLabel": "Policy",
        "truncated": "Showing the newest {{count}} policies.",
        "noPolicies": "There are no policies to choose from."
      },
      "pickModal": {
        "title": "Add from Library",
        "body": "Copies of the picked cases are appended to this suite. It has room for {{count}} more.",
        "selected": "{{count}} selected",
        "submit": "Add {{count}} to Suite"
      }
    }
  },
  "catalog": {
    "allAgents": "Search all agents",
    "allAgentsLocked": "Not currently associated with a specific agent, so all agents are searched.",
    "namespace": "namespace",
    "required": "required"
  }
}
